import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Logs uncaught render errors as structured JSON
 * so they show up in browser consoles in a form a future log forwarder
 * (e.g. CloudWatch RUM) can ingest unchanged.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      JSON.stringify({
        level: 'error',
        msg: 'react_error_boundary',
        error: error.message,
        stack: error.stack,
        component_stack: info.componentStack,
      }),
    );
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <h1 className="text-xl font-semibold text-red-700">Something went wrong.</h1>
            <p className="text-sm text-neutral-600 mt-2">
              The error has been logged. Try reloading the page.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
